import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import {
  consultaAvulsaSchema,
  diagnosticoDetailSchema,
  diagnosticoListSchema,
  onrResponseSchema,
  varreduraIniciadaSchema,
  varreduraStatusSchema,
} from './schemas';

const keys = {
  list: () => ['diagnostico', 'list'] as const,
  detail: (cpf: string) => ['diagnostico', 'detail', cpf] as const,
  varreduraStatus: () => ['diagnostico', 'varredura', 'status'] as const,
};

export function useDiagnosticoList() {
  return useQuery({
    queryKey: keys.list(),
    queryFn: ({ signal }) =>
      api('/api/v2/diagnostico', { schema: diagnosticoListSchema, signal }),
    staleTime: 60_000,
  });
}

export function useDiagnosticoDetail(cpf: string | null) {
  return useQuery({
    queryKey: keys.detail(cpf ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/diagnostico/${cpf}`, { schema: diagnosticoDetailSchema, signal }),
    enabled: !!cpf,
    staleTime: 30_000,
  });
}

export function useVarrerCliente() {
  // Cache invalidation acontece no VarreduraClienteProgressModal.onFinished,
  // disparado quando o polling do status vira 'concluido'. Sem timers cegos.
  return useMutation({
    mutationFn: (cpf: string) =>
      api(`/api/v2/diagnostico/${cpf}/varrer`, {
        method: 'POST',
        schema: varreduraIniciadaSchema,
      }),
  });
}

export function useVarrerTodos() {
  return useMutation({
    mutationFn: () =>
      api('/api/v2/diagnostico/varrer-todos', {
        method: 'POST',
        schema: varreduraIniciadaSchema,
      }),
  });
}

// Polling do progresso. enabled=false desliga o polling quando não há varredura.
export function useVarreduraStatus(enabled: boolean) {
  return useQuery({
    queryKey: keys.varreduraStatus(),
    queryFn: ({ signal }) =>
      api('/api/v2/diagnostico/varrer-todos/status', {
        schema: varreduraStatusSchema,
        signal,
      }),
    enabled,
    refetchInterval: enabled ? 4_000 : false,
    staleTime: 0,
  });
}

// Polling do progresso da rechecagem individual (single-client).
// Backend lê /tmp/diag_progresso_cliente_<cpf-limpo>.json escrito pelo Python.
export function useVarreduraStatusCliente(cpf: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['diagnostico', 'varredura', 'cliente', 'status', cpf ?? ''] as const,
    queryFn: ({ signal }) =>
      api(`/api/v2/diagnostico/${cpf}/varrer/status`, {
        schema: varreduraStatusSchema,
        signal,
      }),
    enabled: enabled && !!cpf,
    refetchInterval: enabled && cpf ? 2_000 : false,
    staleTime: 0,
  });
}

// ONR Sinarma — enriquecimento do detalhe (paralelo, não-bloqueante).
export function useOnr(cpf: string | null) {
  return useQuery({
    queryKey: ['diagnostico', 'onr', cpf ?? ''] as const,
    queryFn: ({ signal }) =>
      api(`/api/v2/diagnostico/${cpf}/ri-digital`, {
        schema: onrResponseSchema,
        signal,
      }),
    enabled: !!cpf,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// Consulta avulsa — CPF que pode não estar na carteira. Bate Python script no back.
export function useConsultaAvulsa() {
  return useMutation({
    mutationFn: (cpf: string) =>
      api(`/api/v2/diagnostico/consulta-avulsa/${encodeURIComponent(cpf)}`, {
        schema: consultaAvulsaSchema,
      }),
  });
}

// Marcar oportunidades como vistas — limpa coluna oportunidade no DB sem alterar score.
export function useMarcarVisto(cpf: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (verificacoes: string[]) =>
      api(`/api/v2/diagnostico/${cpf}/marcar-visto`, {
        method: 'PUT',
        body: { verificacoes },
        schema: z.object({ ok: z.boolean() }),
      }),
    onSuccess: () => {
      if (cpf) {
        void qc.invalidateQueries({ queryKey: keys.detail(cpf) });
        void qc.invalidateQueries({ queryKey: keys.list() });
      }
    },
  });
}
